import React from 'react';

import {assert} from 'Asserts';
import {SystemColumnName, SYSTEM_COLUMN_DEFINITIONS} from 'data-frames/DataFrameConstants';
import {DataFrame} from 'data-frames/DataFrameTypes';
import {DataFrameValueAccessor} from 'data-frames/util/createAccessors';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {VisualizationType} from 'insights/analytics-visualizations/AnalyticsVisualizationTypes';
import makeVisualizationConfig from 'insights/universal-blocks/makeVisualizationConfig';
import {JSONObject} from 'util/Types';

import ModelingDashboardBlock, {SharedModelingBlockProps} from './ModelingDashboardBlock';

export const SELECTED_PREDICTION_FEATURES_TITLE = 'Parameters by data point';
const SELECTED_PREDICTION_FEATURES_DESCRIPTION = '';

/** @package */
export const ACTUAL_OUTPUT_ROW_LABEL = 'Actual output';
/** @package */
export const PREDICTED_OUTPUT_ROW_LABEL = 'Predicted output';

/** @package */
export const VISUALIZATION_TYPE = VisualizationType.TABLE;
/** @package */
export const BASE_VISUALIZATION_PARAMETERS = {
  displayColumnsJson: null,
};

/** @package */
export const PARAMETER_NAME_COLUMN_DEF = {
  systemName: 'parameter_name',
  displayName: 'Parameter Name',
  type: {
    leafType: LeafType.TEXT,
  },
};
/** @package */
export const PARAMETER_VALUE_COLUMN_DEF = {
  systemName: 'parameter_value',
  displayName: 'Parameter Value',
  type: {
    leafType: LeafType.JSON,
  },
};

/**
 * Return a data frame with two columns: "Parameter Name" and "Parameter Value". The features column of the
 * data frame should contain maps of feature names -> feature values, which are spread into "Parameter Name"
 * and "Parameter Value" respectively.
 *
 * @package
 */
export function makeFeaturesDataFrame(
  dataFrame: DataFrame,
  featuresAccessor: DataFrameValueAccessor,
  predictedOutputAccessor: DataFrameValueAccessor,
  dataPointIdAccessor: DataFrameValueAccessor<string>,
  resolveDataPointId: (dataPointId: string) => string,
  selectedDataPointId: string,
  actualOutputAccessor: DataFrameValueAccessor
): DataFrame {
  const selectedAllDataRow = dataFrame.rows.find((row) => dataPointIdAccessor(row) === selectedDataPointId);

  assert(selectedAllDataRow != null, 'Expected selectedDataPointId to be present in the input dataFrame', {
    dataFrame,
    selectedDataPointId,
  });

  const features = featuresAccessor(selectedAllDataRow);

  // Data frames don't have any types to specifically describe a column as a json dict, so we have to hardcode
  // this fact throughout the stack. We check it here incase the assumption ever changes when this function is
  // called.
  assert(typeof features === 'object', 'Expected feature column to contain dicts', {
    features,
    dataFrame,
  });

  return {
    ...dataFrame,
    name: SELECTED_PREDICTION_FEATURES_TITLE,
    columns: [
      PARAMETER_NAME_COLUMN_DEF,
      PARAMETER_VALUE_COLUMN_DEF,
      ...Object.values(SYSTEM_COLUMN_DEFINITIONS),
    ],
    rows: [
      {
        [PARAMETER_NAME_COLUMN_DEF.systemName]: ACTUAL_OUTPUT_ROW_LABEL,
        [PARAMETER_VALUE_COLUMN_DEF.systemName]: actualOutputAccessor(selectedAllDataRow),
        [SystemColumnName.IS_EXCLUDED]: false,
        [SystemColumnName.ROW_ID]: ACTUAL_OUTPUT_ROW_LABEL,
      },
      {
        [PARAMETER_NAME_COLUMN_DEF.systemName]: PREDICTED_OUTPUT_ROW_LABEL,
        [PARAMETER_VALUE_COLUMN_DEF.systemName]: predictedOutputAccessor(selectedAllDataRow),
        [SystemColumnName.IS_EXCLUDED]: false,
        [SystemColumnName.ROW_ID]: PREDICTED_OUTPUT_ROW_LABEL,
      },
      ...Object.entries(features as JSONObject).map(([featureName, featureValue], index) => ({
        [PARAMETER_NAME_COLUMN_DEF.systemName]: featureName,
        [PARAMETER_VALUE_COLUMN_DEF.systemName]: featureValue,
        [SystemColumnName.IS_EXCLUDED]: false,
        [SystemColumnName.ROW_ID]: String(index),
      })),
    ],
  };
}

export interface SelectedPredictionFeaturesBlockProps extends SharedModelingBlockProps {
  dataFrame: DataFrame;
  featuresAccessor: DataFrameValueAccessor;
  predictedOutputAccessor: DataFrameValueAccessor;
  dataPointIdAccessor: DataFrameValueAccessor<string>;
  resolveDataPointId: (dataPointId: string) => string;
  selectedDataPointId: string;
  actualOutputAccessor: DataFrameValueAccessor;
}

export default function SelectedPredictionFeaturesBlock({
  dataFrame,
  featuresAccessor,
  predictedOutputAccessor,
  dataPointIdAccessor,
  resolveDataPointId,
  selectedDataPointId,
  actualOutputAccessor,
  ...sharedModelingBlockProps
}: SelectedPredictionFeaturesBlockProps): JSX.Element {
  const visualizationConfig = makeVisualizationConfig(VISUALIZATION_TYPE, BASE_VISUALIZATION_PARAMETERS);

  const featuresDataFrame = makeFeaturesDataFrame(
    dataFrame,
    featuresAccessor,
    predictedOutputAccessor,
    dataPointIdAccessor,
    resolveDataPointId,
    selectedDataPointId,
    actualOutputAccessor
  );

  return (
    <ModelingDashboardBlock
      {...sharedModelingBlockProps}
      blockTitle={featuresDataFrame.name}
      blockDescription={SELECTED_PREDICTION_FEATURES_DESCRIPTION}
      inputDataFrame={featuresDataFrame}
      visualizationConfig={visualizationConfig}
    />
  );
}
